import React, { useEffect, useState } from 'react';
import { Card } from '../components/Card';
import { dashboardService, ExecutiveSummaryData } from '../services/dashboard.service';
import { formatCompactCurrency, formatCurrency } from '../utils/formatters';

export const ExecutiveSummary: React.FC = () => {
  const [data, setData] = useState<ExecutiveSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const summaryData = await dashboardService.getExecutiveSummary();
        setData(summaryData);
      } catch (err) {
        setError('Failed to load executive summary');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading executive summary...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b-4 border-primary">
        <div className="max-w-full mx-auto px-12 py-6 text-center">
          <h1 className="text-5xl font-bold text-primary mb-2">Executive Summary</h1>
          <p className="text-xl text-text-secondary">
            Strategic Path to March 2026 ({formatCompactCurrency(data.config.march2026Target)}) | FY26 ({formatCompactCurrency(data.config.fy26Target)}) | FY27 ({formatCompactCurrency(data.config.fy27Target)}) EBITDA
          </p>
          <p className="text-sm text-text-muted mt-2">
            Business Plan Overview | Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-12 py-8">
        {/* Key Achievement Box */}
        <div className="bg-background border-4 border-primary rounded-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-primary mb-6 text-center">
            🎯 PATH TO ALL TARGETS
          </h2>
          <div className="grid grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-sm text-text-muted uppercase mb-2">March 2026</div>
              <div className="text-5xl font-bold text-warning my-4">
                {formatCompactCurrency(data.march2026.projection)}
              </div>
              <p className="text-sm text-text-secondary mb-2">
                Target: {formatCompactCurrency(data.config.march2026Target)}
              </p>
              <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                data.march2026.onTrack ? 'bg-success text-white' : 'bg-danger text-white'
              }`}>
                {data.march2026.onTrack ? '✅ ON TRACK' : '⚠️ OFF TRACK'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-text-muted uppercase mb-2">FY 2026 (Apr-Jun)</div>
              <div className="text-5xl font-bold text-success my-4">
                {formatCompactCurrency(data.fy26.projection)}
              </div>
              <p className="text-sm text-text-secondary mb-2">
                Target: {formatCompactCurrency(data.config.fy26Target)}
              </p>
              <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                data.fy26.onTrack ? 'bg-success text-white' : 'bg-danger text-white'
              }`}>
                {data.fy26.onTrack ? '✅ ON TRACK' : '⚠️ OFF TRACK'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-text-muted uppercase mb-2">FY 2027</div>
              <div className="text-5xl font-bold text-primary my-4">
                {formatCompactCurrency(data.fy27.projection)}
              </div>
              <p className="text-sm text-text-secondary mb-2">
                Target: {formatCompactCurrency(data.config.fy27Target)}
              </p>
              <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
                data.fy27.onTrack ? 'bg-success text-white' : 'bg-danger text-white'
              }`}>
                {data.fy27.onTrack ? '✅ ON TRACK' : '⚠️ OFF TRACK'}
              </div>
            </div>
          </div>
        </div>

        {/* EBITDA Progression Section */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            EBITDA Progression
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-background border-2 border-border rounded-lg p-6 text-center">
              <div className="text-lg text-primary font-semibold mb-4">Current (Oct 25)</div>
              <div className="text-5xl font-bold text-primary my-4">
                {formatCompactCurrency(data.config.currentEBITDA)}
              </div>
              <div className="text-sm text-text-muted uppercase">Starting Base</div>
            </div>
            <div className="bg-background border-2 border-warning rounded-lg p-6 text-center">
              <div className="text-lg text-warning font-semibold mb-4">March 2026</div>
              <div className="text-5xl font-bold text-warning my-4">
                {formatCompactCurrency(data.march2026.projection)}
              </div>
              <div className="text-sm text-text-muted uppercase">
                {data.march2026.onTrack ? `On Track for ${formatCompactCurrency(data.config.march2026Target)}` : `Target: ${formatCompactCurrency(data.config.march2026Target)}`}
              </div>
            </div>
            <div className="bg-background border-2 border-success rounded-lg p-6 text-center">
              <div className="text-lg text-success font-semibold mb-4">FY26 (Apr-Jun)</div>
              <div className="text-5xl font-bold text-success my-4">
                {formatCompactCurrency(data.fy26.projection)}
              </div>
              <div className="text-sm text-text-muted uppercase">
                {data.fy26.onTrack ? `On Track for ${formatCompactCurrency(data.config.fy26Target)}` : `Target: ${formatCompactCurrency(data.config.fy26Target)}`}
              </div>
            </div>
            <div className="bg-background border-2 border-primary rounded-lg p-6 text-center">
              <div className="text-lg text-primary font-semibold mb-4">FY27</div>
              <div className="text-5xl font-bold text-primary my-4">
                {formatCompactCurrency(data.fy27.projection)}
              </div>
              <div className="text-sm text-text-muted uppercase">
                {data.fy27.onTrack ? `On Track for ${formatCompactCurrency(data.config.fy27Target)}` : `Target: ${formatCompactCurrency(data.config.fy27Target)}`}
              </div>
            </div>
          </div>
        </section>

        {/* Deal Confidence Breakdown Section */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Deal Pipeline by Confidence Level (All Timelines)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-background border-2 border-success rounded-lg p-6 text-center">
              <div className="text-xl text-success font-semibold mb-4">HIGH Confidence</div>
              <div className="text-6xl font-bold text-success my-4">
                {formatCompactCurrency(data.fy26.pipeline.high || 0)}
              </div>
              <div className="text-sm text-text-muted uppercase">
                {data.fy26.pipeline.dealCount} Deals - {data.fy26.pipeline.total > 0 ? ((data.fy26.pipeline.high / data.fy26.pipeline.total) * 100).toFixed(0) : 0}% of Pipeline
              </div>
            </div>
            <div className="bg-background border-2 border-warning rounded-lg p-6 text-center">
              <div className="text-xl text-warning font-semibold mb-4">MEDIUM Confidence</div>
              <div className="text-6xl font-bold text-warning my-4">
                {formatCompactCurrency(data.fy26.pipeline.medium || 0)}
              </div>
              <div className="text-sm text-text-muted uppercase">
                Deals - {data.fy26.pipeline.total > 0 ? ((data.fy26.pipeline.medium / data.fy26.pipeline.total) * 100).toFixed(0) : 0}% of Pipeline
              </div>
            </div>
            <div className="bg-background border-2 border-danger rounded-lg p-6 text-center">
              <div className="text-xl text-danger font-semibold mb-4">LOW Confidence</div>
              <div className="text-6xl font-bold text-danger my-4">
                {formatCompactCurrency(data.fy26.pipeline.low || 0)}
              </div>
              <div className="text-sm text-text-muted uppercase">
                Deals - {data.fy26.pipeline.total > 0 ? ((data.fy26.pipeline.low / data.fy26.pipeline.total) * 100).toFixed(0) : 0}% of Pipeline
              </div>
            </div>
          </div>

          {/* Pipeline Quality Note */}
          <div className="mt-6 p-4 bg-background border-l-4 border-success rounded">
            <p className="text-text-secondary">
              <strong className="text-success">Strong Pipeline Quality:</strong> {data.fy26.pipeline.total > 0 ? ((data.fy26.pipeline.high / data.fy26.pipeline.total) * 100).toFixed(0) : 0}% of FY26 new deals ({formatCompactCurrency(data.fy26.pipeline.high)}) are HIGH confidence, providing a solid foundation for achieving the {formatCompactCurrency(data.config.fy26Target)} target. MEDIUM and LOW confidence deals represent upside opportunities.
            </p>
          </div>
        </section>

        {/* Path to FY26 EBITDA Section */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <div className="bg-background border-4 border-success rounded-lg p-6 mb-6">
            <h2 className="text-3xl font-bold text-success mb-6">
              ✅ Path to {formatCompactCurrency(data.fy26.projection)} EBITDA - JUNE FY26 PROJECTION
            </h2>
          </div>

          {/* FY26 Waterfall */}
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              FY26 EBITDA Build-Up (Waterfall)
            </h3>
            <div className="space-y-4">
              {data.waterfall.fy26.map((item, index) => {
                const isTarget = item.label.includes('Target');
                const isProjection = item.label.includes('Projection');
                const progress =
                  isTarget || isProjection ? 100 : (item.value / data.config.fy26Target) * 100;

                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-sm font-medium ${
                          isTarget
                            ? 'text-warning'
                            : isProjection
                            ? 'text-primary'
                            : 'text-text-primary'
                        }`}
                      >
                        {item.label}
                      </span>
                      <span
                        className={`text-sm font-bold ${
                          isTarget
                            ? 'text-warning'
                            : isProjection
                            ? 'text-primary'
                            : 'text-text-primary'
                        }`}
                      >
                        {formatCurrency(item.value)}
                      </span>
                    </div>
                    <div className="w-full bg-surface-light rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${
                          isTarget
                            ? 'bg-warning'
                            : isProjection
                            ? 'bg-primary'
                            : item.value > 0
                            ? 'bg-success'
                            : 'bg-danger'
                        }`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Gap Warning */}
            {!data.fy26.onTrack && (
              <div className="mt-6 p-4 bg-danger bg-opacity-10 border border-danger rounded-lg">
                <p className="text-danger font-semibold">
                  ⚠ Gap to Target: {formatCurrency(Math.abs(data.fy26.gap))} (
                  {Math.abs(data.fy26.gapPercentage).toFixed(1)}%)
                </p>
                <p className="text-sm text-text-muted mt-1">
                  Additional pipeline or cost savings needed to reach FY26 target
                </p>
              </div>
            )}

            {/* Success Message */}
            {data.fy26.onTrack && (
              <div className="mt-6 p-4 bg-success bg-opacity-10 border border-success rounded-lg">
                <p className="text-success font-semibold">
                  ✓ FY26 Target Achieved - Exceeding by{' '}
                  {formatCurrency(Math.abs(data.fy26.gap))}
                </p>
              </div>
            )}
          </Card>
        </section>

        {/* FY27 Target Section */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <div className="bg-background border-4 border-success rounded-lg p-6 mb-6">
            <h2 className="text-3xl font-bold text-success mb-6">
              ✅ Path to {formatCompactCurrency(data.fy27.projection)} EBITDA - FY27 PROJECTION
            </h2>
          </div>

          {/* FY27 Waterfall */}
          <Card>
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              FY27 EBITDA Build-Up (Waterfall)
            </h3>
            <div className="space-y-4">
              {data.waterfall.fy27.map((item, index) => {
                const isTarget = item.label.includes('Target');
                const isProjection = item.label.includes('Projection');
                const progress =
                  isTarget || isProjection ? 100 : (item.value / data.config.fy27Target) * 100;

                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-sm font-medium ${
                          isTarget
                            ? 'text-warning'
                            : isProjection
                            ? 'text-primary'
                            : 'text-text-primary'
                        }`}
                      >
                        {item.label}
                      </span>
                      <span
                        className={`text-sm font-bold ${
                          isTarget
                            ? 'text-warning'
                            : isProjection
                            ? 'text-primary'
                            : 'text-text-primary'
                        }`}
                      >
                        {formatCurrency(item.value)}
                      </span>
                    </div>
                    <div className="w-full bg-surface-light rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${
                          isTarget
                            ? 'bg-warning'
                            : isProjection
                            ? 'bg-primary'
                            : item.value > 0
                            ? 'bg-success'
                            : 'bg-danger'
                        }`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Gap Warning */}
            {!data.fy27.onTrack && (
              <div className="mt-6 p-4 bg-danger bg-opacity-10 border border-danger rounded-lg">
                <p className="text-danger font-semibold">
                  ⚠ Gap to Target: {formatCurrency(Math.abs(data.fy27.gap))} (
                  {Math.abs(data.fy27.gapPercentage).toFixed(1)}%)
                </p>
                <p className="text-sm text-text-muted mt-1">
                  Additional deals or acquisitions needed to reach FY27 target
                </p>
              </div>
            )}

            {/* Success Message */}
            {data.fy27.onTrack && (
              <div className="mt-6 p-4 bg-success bg-opacity-10 border border-success rounded-lg">
                <p className="text-success font-semibold">
                  ✓ FY27 Target Achieved - Exceeding by{' '}
                  {formatCurrency(Math.abs(data.fy27.gap))}
                </p>
              </div>
            )}
          </Card>
        </section>

        {/* Department Breakdown */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            FY26 Pipeline by Department
          </h2>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-text-secondary">
                      Department
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">
                      Deal Count
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">
                      Pipeline Value
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-text-secondary">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.breakdown.byDepartment)
                    .sort(([, a], [, b]) => b.value - a.value)
                    .map(([dept, stats]) => (
                      <tr
                        key={dept}
                        className="border-b border-border hover:bg-surface-light transition-colors"
                      >
                        <td className="py-4 px-4">
                          <p className="font-medium text-text-primary">{dept}</p>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary bg-opacity-20 text-primary text-sm font-medium">
                            {stats.count}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <p className="font-semibold text-primary">
                            {formatCurrency(stats.value)}
                          </p>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <p className="text-text-muted">
                            {data.fy26.pipeline.total > 0 ? ((stats.value / data.fy26.pipeline.total) * 100).toFixed(1) : 0}%
                          </p>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t-2 border-border bg-surface">
        <div className="max-w-full mx-auto px-12 py-6 text-center">
          <p className="text-text-muted">
            Unique X - Executive Summary
          </p>
          <p className="text-sm text-text-muted mt-2">
            Strategic EBITDA growth tracking with real-time pipeline monitoring
          </p>
        </div>
      </footer>
    </div>
  );
};
